import { useBuilder } from "hec/hooks/useBuilder";
import { useReference } from "hec/hooks/useReferences";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Await, useNavigate } from "react-router";

import { type RegisteredComponent, fetchOneEntry } from "@builder.io/sdk-react";

import { Button } from "~/components/ui/button";
import { ModelMap } from "~/hec/builder/model-map";
import { Heading } from "~/hec/components/ui/heading";
import useLearningCenterMutation from "~/hooks/learning-center/use-learning-center-mutation";
import useLearningCenter from "~/hooks/use-learning-center";
import { cn } from "~/lib/ui";

/**
 * SFCC-11728 — port of hec-composable `hec/builder/blocks/goal/goalTile.jsx`.
 *
 * The landing `goal-based-learning-section` tile that lets a shopper START a
 * goal. Clicking an un-started tile calls `startGoal` (or `openGoal` when a
 * goal is already active), persists progress (guest → localStorage; registered
 * → SCAPI `c_learningCenterData`, both via `useLearningCenter`), then navigates
 * to the goal dashboard page. Starting the Enroll goal seeds
 * `activeGoal`/`activeLessonUrl`, which flips the hero "Complete Lessons" CTA
 * from its scroll fallback to a real navigation into the enroll module.
 *
 * Before this block existed the tiles rendered empty (unregistered Builder
 * block) so the guest could never start the goal.
 *
 * `noWrap` block — registered as "GoalTile" (the name live Builder content
 * references) and MUST spread `attributes` on its root element.
 *
 * Deltas vs composable:
 *  - The `goalRef` (model `learning-center-goal`) is resolved SERVER-SIDE via
 *    the `$.tsx` loader's `fetchReferences` (surfaced through `useReference`),
 *    not a client `useFetchBuilderReference` — this avoids the enrich
 *    nested-ref pitfall (SFCC-11308) and renders the tile on first paint when
 *    Builder inlines `goalRef.value.data`. We accept both shapes.
 *  - RR7 `useNavigate` replaces `useHistory().push`.
 *  - `getActiveGoal()` is called with no arg for both guest + registered: the
 *    Workers `useLearningCenter` already resolves the correct source
 *    (localStorage for guests, SCAPI for registered) — there is no
 *    `useCustomerType` branch to feed it the goal entry as the composable did.
 *  - The goal's NESTED `dashboard` reference is NOT resolved by the loader
 *    (its `fetchReferences` pass uses `enrichLevel: 0`, which strips nested ref
 *    values — the SFCC-11308 guard we must NOT change). So the click had no URL
 *    to navigate to (SFCC-11732). `GoalTileInner` now resolves
 *    `dashboard.value.data.url` client-side by `dashboard.id` when it isn't
 *    inlined — mirroring composable's `useSelectedGoalData` fallback fetch
 *    (`fields: 'data.url'`).
 */

const constants = {
  Cta: "Learn More",
  ContinueCta: "Continue",
  ReviewCta: "Review",
} as const;

/**
 * A nested Builder reference to the goal's dashboard page. When Builder inlines
 * one level deep (`includeRefs`) the `value.data.url` is present (fast path);
 * when the SSR loader resolves the parent goal with `enrichLevel: 0` the nested
 * ref is left as a bare stub (`id`/`model`, no `value`) and must be resolved
 * client-side by `id` (SFCC-11732).
 */
interface DashboardReference {
  id?: string;
  model?: string;
  value?: { data?: { url?: string } };
}

/** The resolved `learning-center-goal` entry `data` payload. */
interface GoalData {
  id?: string;
  title?: string;
  description?: string;
  image?: string;
  dashboard?: DashboardReference;
}

/** A Builder reference input — resolved (`value.data`) or unresolved (`id`). */
interface GoalReference {
  id?: string;
  model?: string;
  value?: { data?: GoalData };
}

interface GoalTileProps {
  goalRef?: GoalReference;
  attributes?: Record<string, unknown>;
  "data-testid"?: string;
}

/**
 * Presentational + behavior layer — receives the fully resolved goal `data`.
 * Reads the learner's progress, derives the CTA label/highlight, and on click
 * starts/opens the goal then navigates to its dashboard. Exported for unit
 * tests (the outer `GoalTile` only adds reference resolution).
 */
export const GoalTileInner = ({
  goalData,
  attributes,
  "data-testid": dataTestId,
}: {
  goalData: GoalData;
  attributes?: Record<string, unknown>;
  "data-testid"?: string;
}) => {
  const { className, ...restAttributes } = (attributes ?? {}) as {
    className?: string;
  } & Record<string, unknown>;
  const [requestInProgress, setRequestInProgress] = useState(false);
  const navigate = useNavigate();
  const { config } = useBuilder();
  const { learningCenterData, getActiveGoal, getGoalByID } =
    useLearningCenter();
  const { startGoal, openGoal } = useLearningCenterMutation();

  const { id: goalId, title, description, dashboard, image } = goalData ?? {};

  // The goal's dashboard URL. Two resolution paths (SFCC-11732):
  //  - Fast path: Builder inlined the nested ref one level deep
  //    (`includeRefs`) so `dashboard.value.data.url` is already present.
  //  - Fallback: the `$.tsx` loader resolved the parent goal with
  //    `enrichLevel: 0`, which STRIPS nested ref values (SFCC-11308 guard —
  //    don't change that shared resolver), leaving `dashboard` a bare
  //    `{ id, model }` stub. Mirror composable's `useSelectedGoalData` and
  //    resolve it client-side by `dashboard.id` (fields: `data.url`).
  const inlinedUrl = dashboard?.value?.data?.url;
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(
    inlinedUrl,
  );
  const goalPageUrl = inlinedUrl ?? resolvedUrl;
  // Shared in-flight resolution so a fast click can await the SAME fetch the
  // mount effect kicked off, rather than firing a second one or no-oping.
  const dashboardFetchRef = useRef<Promise<string | undefined> | null>(null);

  const dashboardId = dashboard?.id;
  const dashboardModel = dashboard?.model ?? ModelMap.learningCenterPage;
  const apiKey = config.api_key;

  const resolveDashboardUrl = useCallback(async (): Promise<
    string | undefined
  > => {
    if (inlinedUrl) return inlinedUrl;
    if (!dashboardId || !apiKey) return undefined;
    if (!dashboardFetchRef.current) {
      dashboardFetchRef.current = fetchOneEntry({
        model: dashboardModel,
        apiKey,
        userAttributes: { useScheduling: true },
        options: { query: { id: dashboardId }, limit: 1, fields: "data.url" },
        fetchOptions: { signal: AbortSignal.timeout(2500) },
      })
        .then(entry => {
          const url = (entry?.data as { url?: string } | undefined)?.url;
          return url;
        })
        .catch(error => {
          console.error(
            `[GoalTile] Failed to resolve dashboard ref ${dashboardId}:`,
            error,
          );
          return undefined;
        });
    }
    return dashboardFetchRef.current;
  }, [apiKey, dashboardId, dashboardModel, inlinedUrl]);

  // Resolve eagerly on mount so the click navigates synchronously in the common
  // case. `handleClick` still awaits as a backstop for a click before this
  // settles.
  useEffect(() => {
    if (inlinedUrl || !dashboardId) return;
    let cancelled = false;
    void resolveDashboardUrl().then(url => {
      if (!cancelled && url) setResolvedUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [inlinedUrl, dashboardId, resolveDashboardUrl]);

  const activeGoal = getActiveGoal();
  const sfccGoal = goalId ? getGoalByID(goalId) : null;
  const isOpened = !!sfccGoal?.open;
  const isStarted = !!sfccGoal?.start;
  const isCompleted = !!sfccGoal?.complete;
  const isMostRecentOpened =
    isOpened && goalId === learningCenterData?.activeGoal;

  const ctaText = isCompleted
    ? constants.ReviewCta
    : isStarted || isOpened
      ? constants.ContinueCta
      : constants.Cta;

  const highlightedBtnClasses = isMostRecentOpened
    ? "bg-emphasis text-white hover:text-white"
    : "";

  const handleClick = useCallback(async () => {
    if (!goalId) return;
    // No active goal yet → this tile starts the goal (seeds activeGoal).
    // A goal already active → just open this one (no new active goal).
    const shouldStartGoal = !activeGoal;
    setRequestInProgress(true);
    try {
      const response = shouldStartGoal
        ? await startGoal(goalId)
        : await openGoal(goalId);
      if (!response.success) {
        throw new Error(response.errorMsg ?? "Failed to start goal");
      }
    } finally {
      setRequestInProgress(false);
    }
    // Navigate after the mutation. The dashboard ref is usually resolved on
    // mount; if a fast click beats that, await the same in-flight resolution so
    // we still navigate instead of silently no-oping (SFCC-11732).
    const targetUrl = goalPageUrl ?? (await resolveDashboardUrl());
    if (targetUrl) navigate(targetUrl);
  }, [
    activeGoal,
    goalId,
    goalPageUrl,
    navigate,
    openGoal,
    resolveDashboardUrl,
    startGoal,
  ]);

  return (
    <div
      {...restAttributes}
      className={cn("flex flex-col items-start", className)}
      data-testid={dataTestId ?? "goal-tile"}
    >
      {image && (
        <img
          src={image}
          alt={title ?? ""}
          className="mb-2 h-32 w-full object-cover lg:mb-4 lg:h-60"
        />
      )}
      <Heading
        variant="h6"
        className="mb-1 leading-4"
        data-testid="goal-tile-heading"
      >
        {title}
      </Heading>
      <div className="mb-2 text-xs leading-3 lg:text-base lg:leading-6">
        <p>{description}</p>
      </div>
      <div className="mt-auto">
        <Button
          type="button"
          variant="secondary"
          className={cn("px-8", highlightedBtnClasses)}
          onClick={handleClick}
          disabled={requestInProgress}
          data-testid="goal-tile-button"
        >
          {ctaText}
        </Button>
      </div>
    </div>
  );
};

/**
 * Outer block: resolves the `goalRef`. Fast-path on the SSR-inlined
 * `goalRef.value.data` (Builder `includeRefs`/enrich); otherwise await the
 * server-resolved reference from the route loader's `references` map.
 */
const GoalTile = ({
  goalRef,
  attributes,
  "data-testid": dataTestId,
}: GoalTileProps) => {
  const inlined = goalRef?.value?.data;
  const referenceId = goalRef?.id ?? "";
  // Always call the hook (Rules of Hooks); only consume it when not inlined.
  const reference = useReference(referenceId);

  // No goal wired on the entry (e.g. the Builder editor canvas) — mirror the
  // other LC blocks' hint rather than an empty band.
  if (!referenceId && !inlined) {
    return (
      <div {...(attributes ?? {})} data-testid="goal-tile-empty">
        Please select a goal
      </div>
    );
  }

  if (inlined) {
    return (
      <GoalTileInner
        goalData={inlined}
        attributes={attributes}
        data-testid={dataTestId}
      />
    );
  }

  if (!reference) return null;

  return (
    <Suspense key={referenceId}>
      <Await resolve={reference}>
        {resolved => {
          const goalData = resolved?.content?.data as GoalData | undefined;
          if (!goalData) return null;
          return (
            <GoalTileInner
              goalData={goalData}
              attributes={attributes}
              data-testid={dataTestId}
            />
          );
        }}
      </Await>
    </Suspense>
  );
};

export const GoalTileDefinition = {
  component: GoalTile,
  name: "GoalTile",
  noWrap: true,
  inputs: [
    {
      name: "goalRef",
      type: "reference",
      model: ModelMap.learningCenterGoal,
      friendlyName: "Select Goal",
      required: true,
    },
  ],
} satisfies RegisteredComponent;

export default GoalTile;
